import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, ChevronRight, CheckCircle, Waves } from "lucide-react";
import Navigation from "@/components/Navigation";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import HeroSection from "@/components/HeroSection";
import Footer from "@/components/Footer";
import WaveDivider from "@/components/WaveDivider";
import SEO from "@/components/SEO";
import ScrollReveal from "@/components/ScrollReveal";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [contactMethod, setContactMethod] = useState("email");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    course_interest: ""
  });
  const [loading, setLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [bookingDiveSite, setBookingDiveSite] = useState(null);
  const [bookingDiveCenter, setBookingDiveCenter] = useState(null);
  const [bookingPackage, setBookingPackage] = useState(null);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const response = await axios.get(`${API}/current-location`);
        setCurrentLocation(response.data);
      } catch (error) {
        console.error("Error loading location:", error);
      }
    };
    
    // Load dive site, dive center, and package from sessionStorage
    const storedDiveSite = sessionStorage.getItem('bookingDiveSite');
    const storedDiveCenter = sessionStorage.getItem('bookingDiveCenter');
    const storedPackage = sessionStorage.getItem('bookingPackage');
    
    let messageText = '';
    
    if (storedDiveCenter) {
      const diveCenter = JSON.parse(storedDiveCenter);
      setBookingDiveCenter(diveCenter);
      messageText = `I'm interested in booking with ${diveCenter.name}`;
      if (diveCenter.location) {
        messageText += ` (${diveCenter.location}, ${diveCenter.country})`;
      }
      messageText += '. ';
    }
    
    if (storedPackage) {
      const packageInfo = JSON.parse(storedPackage);
      setBookingPackage(packageInfo);
      messageText += `I would like to book: ${packageInfo.name}`;
      if (packageInfo.price_euro) {
        messageText += ` (€${packageInfo.price_euro})`;
      }
      messageText += '. ';
    }
    
    if (storedDiveSite) {
      const diveSite = JSON.parse(storedDiveSite);
      setBookingDiveSite(diveSite);
      messageText += `I would like to dive at ${diveSite.name}. `;
    }
    
    if (messageText) {
      setFormData(prev => ({
        ...prev,
        message: messageText
      }));
    }

    fetchLocation();
    
    // Cleanup on unmount
    return () => {
      sessionStorage.removeItem('bookingDiveSite');
      sessionStorage.removeItem('bookingDiveCenter');
      sessionStorage.removeItem('bookingPackage');
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      
      // Track Google Ads conversion
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17779264927/MjBLCM7h2csbEJ-b6J1C'
        });
      }
      
      setFormSubmitted(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendAnother = () => {
    setFormSubmitted(false);
    setFormData({
      name: "",
      email: "",
      phone: "",
      message: "",
      course_interest: ""
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Contact George Dives - Get In Touch for Diving Courses & Packages"
        description="Ready to start your diving adventure? Contact George for PADI courses, fun diving packages, and personalized diving experiences. Email or WhatsApp available."
        keywords={['contact dive instructor', 'diving inquiries', 'PADI course booking', 'dive package information']}
      />
      
      <Navigation />
      <FloatingUserMenu />

      {/* Header Section */}
      <HeroSection 
        title="Get in Touch,"
        subtitle="Start Your Journey"
        description="Have questions or ready to book? Send me a message and I'll respond within 24 hours."
        backgroundImage="https://images.unsplash.com/photo-1559827260-dc66d52bef19?crop=entropy&cs=srgb&fm=jpg&q=85"
      />
      <WaveDivider variant="smooth" />

      {/* Contact Section */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <Card className="border-2">
                <CardHeader>
                  <CardTitle>Send a Message</CardTitle>
                  <CardDescription>Choose your preferred contact method</CardDescription>
                  
                  {/* Booking Context Indicators */}
                  {(bookingDiveCenter || bookingDiveSite || bookingPackage) && (
                    <div className="mt-3 space-y-2">
                      {bookingDiveCenter && (
                        <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-cyan-600" />
                          <div>
                            <p className="text-xs text-cyan-600 font-semibold">Dive Center:</p>
                            <p className="text-sm text-cyan-800 font-bold">{bookingDiveCenter.name}</p>
                            {bookingDiveCenter.location && (
                              <p className="text-xs text-cyan-600">{bookingDiveCenter.location}, {bookingDiveCenter.country}</p>
                            )}
                          </div>
                        </div>
                      )}
                      {bookingPackage && (
                        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2 flex-1">
                              <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5" />
                              <div>
                                <p className="text-xs text-purple-600 font-semibold">Selected Package:</p>
                                <p className="text-sm text-purple-800 font-bold">{bookingPackage.name}</p>
                                {bookingPackage.description && (
                                  <p className="text-xs text-purple-600 mt-1">{bookingPackage.description}</p>
                                )}
                              </div>
                            </div>
                            {bookingPackage.price_euro && (
                              <div className="text-right">
                                <p className="text-lg font-bold text-purple-800">€{bookingPackage.price_euro}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {bookingDiveSite && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                          <Waves className="w-4 h-4 text-green-600" />
                          <div>
                            <p className="text-xs text-green-600 font-semibold">Selected Dive Site:</p>
                            <p className="text-sm text-green-800 font-bold">{bookingDiveSite.name}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Contact Method Tabs */}
                  <div className="flex gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setContactMethod("email")}
                      className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                        contactMethod === "email"
                          ? "bg-cyan-500 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      data-testid="contact-email-tab"
                    >
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={() => setContactMethod("whatsapp")}
                      className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                        contactMethod === "whatsapp"
                          ? "bg-green-500 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      data-testid="contact-whatsapp-tab"
                    >
                      <svg className="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      WhatsApp
                    </button>
                  </div>
                </CardHeader>
                <CardContent>
                  {contactMethod === "email" ? (
                    formSubmitted ? (
                      <div className="space-y-6 py-8 text-center" data-testid="form-success-message">
                        <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                          <CheckCircle className="w-10 h-10 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-3">Message Sent Successfully!</h3>
                        <p className="text-lg text-slate-600 mb-6">
                          Thank you for reaching out! I'll get back to you within 24 hours.
                        </p>
                        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-6 mb-6">
                          <p className="text-sm text-cyan-800">
                            <strong>What happens next?</strong> I'll review your message and respond to your email address with more details about your diving adventure.
                          </p>
                        </div>
                        <Button 
                          onClick={handleSendAnother}
                          className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                          data-testid="send-another-btn"
                        >
                          Send Another Message
                        </Button>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                        <div>
                          <Label htmlFor="name">Name *</Label>
                          <Input 
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            placeholder="Your full name"
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email *</Label>
                          <Input 
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="your.email@example.com"
                            data-testid="contact-email-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone">Phone (Optional)</Label>
                          <Input 
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+1 234 567 8900"
                            data-testid="contact-phone-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="course_interest">Course/Package Interest</Label>
                          <Input 
                            id="course_interest"
                            name="course_interest"
                            value={formData.course_interest}
                            onChange={handleInputChange}
                            placeholder="e.g., Open Water Diver"
                            data-testid="contact-course-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="message">Message *</Label>
                          <Textarea 
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            required
                            placeholder="Tell me about your diving experience and what you're looking for..."
                            rows={5}
                            data-testid="contact-message-input"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                          disabled={loading}
                          data-testid="contact-submit-btn"
                        >
                          {loading ? "Sending..." : "Send Message"}
                        </Button>
                      </form>
                    )
                  ) : (
                    <div className="space-y-6 py-8">
                      <div className="text-center">
                        <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                          <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                          </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-3">Chat on WhatsApp</h3>
                        <p className="text-slate-600 mb-6">
                          Get instant responses! Click below to start a WhatsApp conversation with me.
                        </p>
                        <a
                          href="https://wa.me/306987946278"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-8 py-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-xl"
                          data-testid="whatsapp-button"
                        >
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                          </svg>
                          Open WhatsApp
                        </a>
                        <p className="text-sm text-slate-500 mt-4">
                          +30 698 794 6278
                        </p>
                      </div>
                      
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-sm text-green-800">
                          <strong>Why WhatsApp?</strong> Get quick answers, share photos, and have a more personal conversation about your diving plans!
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white p-8 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Email</p>
                      <a href="mailto:info@georgedives.com" className="text-sm text-cyan-100 hover:text-white">
                        info@georgedives.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Phone</p>
                      <a href="tel:+306987946278" className="text-sm text-cyan-100 hover:text-white">
                        +30 698 794 6278
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Currently</p>
                      <p className="text-sm text-cyan-100">
                        {currentLocation ? `${currentLocation.location_name}, ${currentLocation.country}` : 'Teaching worldwide'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Social Media Links */}
                <div className="mt-6 pt-6 border-t border-white/20">
                  <p className="font-semibold mb-4">Follow Me</p>
                  <div className="flex gap-3">
                    {/* Instagram */}
                    <a 
                      href="https://www.instagram.com/george_dives?igsh=MXR3YmhwN2h0Nmk4eA==" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                      data-testid="social-instagram"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </a>
                    
                    {/* Facebook */}
                    <a 
                      href="https://www.facebook.com/share/1Q8HzKKNWL/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                      data-testid="social-facebook"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-100 p-8 rounded-xl">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Why Choose Me?</h3>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">PADI Staff Instructor with 10+ years experience</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Small class sizes for personalized attention</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Partnership with premier dive centers worldwide</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Flexible scheduling and custom packages available</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;